import { BlurFilter, Container, FillGradient, Graphics } from "pixi.js";

import type {
  IOverlayElementShape,
  IOverlayFill,
  IOverlayGlow,
  IOverlayStrokeStyle,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

interface IShapeRenderArgs {
  element: IOverlayElementShape;

  destination: { width: number; height: number };
}

export class OverlayShapeRenderer {
  render(container: Container, args: IShapeRenderArgs): void {
    const { element, destination } = args;

    const shape = element.options.shape;

    const w = destination.width;

    const h = destination.height;

    const fill = element.options.fill;

    const stroke = element.options.stroke;

    const strokeWidth = element.options.strokeWidth ?? 0;

    const strokeStyle: IOverlayStrokeStyle =
      element.options.strokeStyle ?? "solid";

    const borderRadius = element.options.borderRadius ?? 0;

    const glow = element.glow;

    const wrapper = new Container();

    // Glow goes behind the main graphics so the bright tint bleeds outward
    if (glow) {
      this.applyGlow(wrapper, shape, w, h, borderRadius, glow);
    }

    const g = new Graphics();

    this.drawPath(g, shape, w, h, borderRadius);

    this.applyFill(g, fill, w, h);

    wrapper.addChild(g);

    this.applyStroke(
      wrapper,
      g,
      stroke,
      strokeWidth,
      strokeStyle,
      shape,
      w,
      h,
      borderRadius,
    );

    // Position + rotate around the bounds' center so visual rotation matches DOM
    if (element.rotation) {
      const rad = (element.rotation * Math.PI) / 180;

      const pivotX = w / 2;

      const pivotY = h / 2;

      wrapper.pivot.set(pivotX, pivotY);

      wrapper.position.set(
        element.bounds.left + pivotX,
        element.bounds.top + pivotY,
      );

      wrapper.rotation = rad;
    } else {
      wrapper.position.set(element.bounds.left, element.bounds.top);
    }

    container.addChild(wrapper);
  }

  private drawPath(
    g: Graphics,
    shape: IOverlayElementShape["options"]["shape"],
    w: number,
    h: number,
    borderRadius: number,
  ): void {
    switch (shape) {
      case "rect":
        g.rect(0, 0, w, h);

        break;

      case "rounded-rect":
        g.roundRect(0, 0, w, h, borderRadius);

        break;

      case "circle": {
        const r = Math.min(w, h) / 2;

        g.circle(w / 2, h / 2, r);

        break;
      }

      case "hexagon": {
        // Flat-top hexagon inscribed in the bounding rect
        const cx = w / 2;

        const cy = h / 2;

        const rx = w / 2;

        const ry = h / 2;

        const points: number[] = [];

        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i;

          points.push(cx + rx * Math.cos(angle), cy + ry * Math.sin(angle));
        }

        g.poly(points);

        break;
      }

      case "parallelogram": {
        // ~15deg slant: top-left/bottom-right shifted by tan(15deg) * height
        const slant = Math.tan((15 * Math.PI) / 180) * h;

        g.poly([slant, 0, w, 0, w - slant, h, 0, h]);

        break;
      }

      case "chevron": {
        // Right-pointing arrow. Tip depth = 25% of width.
        const tip = w * 0.25;

        const halfH = h / 2;

        g.poly([0, 0, w - tip, 0, w, halfH, w - tip, h, 0, h]);

        break;
      }

      default:
        g.rect(0, 0, w, h);
    }
  }

  private applyFill(
    g: Graphics,
    fill: string | IOverlayFill | undefined,
    w: number,
    h: number,
  ): void {
    if (!fill) return;

    if (typeof fill === "string") {
      g.fill({ color: fill });

      return;
    }

    if (fill.kind === "solid") {
      g.fill({ color: fill.color });

      return;
    }

    if (fill.kind === "linear" || fill.kind === "radial") {
      const fallback = fill.stops[0]?.color ?? "#ffffff";

      try {
        const colorStops = fill.stops.map((s) => ({
          offset: s.position / 100,

          color: s.color,
        }));

        // Local-space (0..1) coords are the path Pixi v8's gradient
        // pipeline handles most reliably — the global-coord path
        // dropped the warm/teal blooms entirely on shapes whose
        // bounds extend past the parent area. Scale the texture to
        // the shape's dimensions so big blooms (e.g. 1120×1120) get
        // enough samples to render smoothly without banding.
        const textureSize = Math.max(
          256,
          Math.min(2048, Math.round(Math.max(w, h))),
        );

        if (fill.kind === "linear") {
          // CSS angle convention:
          //  · 0deg → "to top"    → direction (0, -1)
          //  · 90deg → "to right" → direction (1, 0)
          //  · 180deg → "to bottom" → direction (0, 1)
          // Translates to (sin θ, -cos θ) in unit space.
          const angleRad = ((fill.angle ?? 0) * Math.PI) / 180;
          const dx = Math.sin(angleRad);
          const dy = -Math.cos(angleRad);

          // Project the bounding box onto the direction vector to
          // find the gradient axis length expressed in normalized
          // (0..1) units of the shape's bounds. CSS standard.
          const lenU =
            Math.abs(Math.sin(angleRad)) +
            Math.abs(Math.cos(angleRad)) * (h / Math.max(1, w));
          const halfU = lenU / 2;

          const start = { x: 0.5 - dx * halfU, y: 0.5 - dy * halfU };
          const end = { x: 0.5 + dx * halfU, y: 0.5 + dy * halfU };

          const grad = new FillGradient({
            type: "linear",
            start,
            end,
            colorStops,
            textureSpace: "local",
            textureSize,
          });

          g.fill(grad);
        } else {
          // CSS `radial-gradient(circle, ...)` with farthest-corner
          // default would map the 100% stop to the bounding box
          // corner (≈0.707 in normalized local space). Pixi v8
          // Graphics's radial pipeline interpolates the stops over
          // its `outerRadius` distance, but with `textureSpace:
          // "local"` and a circular shape that distance becomes
          // visually compressed inside the inscribed circle — the
          // first stop ends up much further from center than authors
          // expect. Concretely, the BRB Synthwave "sun" radial
          // (0% magenta, 60% alpha 0.4, 100% transparent) rendered
          // as a transparent ring with no opaque core because the
          // entire 0..0.5 radius (the visible part of the inscribed
          // circle) was sampling beyond stop 60%.
          //
          // Fix: keep outerRadius at 1.0 (the unit-circle edge of
          // local space). The shape's own circle clip naturally
          // limits the visible portion to radius 0.5, so the stops
          // map exactly the same way CSS does for a circular shape.
          const grad = new FillGradient({
            type: "radial",
            center: { x: 0.5, y: 0.5 },
            innerRadius: 0,
            outerCenter: { x: 0.5, y: 0.5 },
            outerRadius: 1,
            colorStops,
            textureSpace: "local",
            textureSize,
          });

          g.fill(grad);
        }
      } catch {
        g.fill({ color: fallback });
      }
    }
  }

  /**
   * Pixi v8 has no native dashed stroke. We simulate it by sampling
   * the shape's outline into a dense polyline of points and walking
   * it with alternating draw/skip segments. The CSS analogues:
   *  · solid  → continuous stroke (Pixi native)
   *  · dashed → 12px on / 8px off
   *  · dotted → 2px on / 6px off (round caps to read as round dots)
   *
   * Sampling 192 points around any closed path is enough resolution
   * for the seed shapes (hexagons, circles, rects). Cheaper than a
   * shader and keeps the renderer DOM-free.
   */
  private applyStroke(
    wrapper: Container,
    g: Graphics,
    stroke: string | undefined,
    strokeWidth: number,
    strokeStyle: IOverlayStrokeStyle,
    shape: IOverlayElementShape["options"]["shape"],
    w: number,
    h: number,
    borderRadius: number,
  ): void {
    if (!stroke || strokeWidth <= 0) return;

    if (strokeStyle === "solid" || !strokeStyle) {
      g.stroke({ color: stroke, width: strokeWidth });
      return;
    }

    // "double": dos contornos concéntricos con un hueco entre ellos. Pixi v8 no
    // tiene border-style:double, así que dibujamos el path exterior + una copia
    // escalada hacia adentro (misma técnica de escala que applyGlow). Antes caía
    // al sampler de dashes y salía punteado.
    if (strokeStyle === "double") {
      const band = Math.max(1, strokeWidth / 3);
      const outer = new Graphics();
      this.drawPath(outer, shape, w, h, borderRadius);
      outer.stroke({ color: stroke, width: band });
      wrapper.addChild(outer);

      const inset = strokeWidth; // separación total exterior↔interior
      const sx = w > 0 ? (w - inset * 2) / w : 1;
      const sy = h > 0 ? (h - inset * 2) / h : 1;
      if (sx > 0 && sy > 0) {
        const inner = new Graphics();
        this.drawPath(inner, shape, w, h, borderRadius);
        inner.stroke({ color: stroke, width: band });
        inner.pivot.set(w / 2, h / 2);
        inner.position.set(w / 2, h / 2);
        inner.scale.set(sx, sy);
        wrapper.addChild(inner);
      }
      return;
    }

    const points = this.samplePathPoints(shape, w, h, borderRadius);
    if (points.length < 2) {
      g.stroke({ color: stroke, width: strokeWidth });
      return;
    }

    const dash = strokeStyle === "dotted" ? 2 : 12;
    const gap = strokeStyle === "dotted" ? 6 : 8;
    const cap = strokeStyle === "dotted" ? "round" : "butt";

    const dashed = new Graphics();
    let drawing = true;
    let remaining = dash;

    let prev = points[0];
    dashed.moveTo(prev.x, prev.y);

    for (let i = 1; i < points.length; i++) {
      const next = points[i];
      let segDx = next.x - prev.x;
      let segDy = next.y - prev.y;
      let segLen = Math.hypot(segDx, segDy);

      while (segLen > remaining) {
        const t = remaining / segLen;
        const cx = prev.x + segDx * t;
        const cy = prev.y + segDy * t;
        if (drawing) {
          dashed.lineTo(cx, cy);
        } else {
          dashed.moveTo(cx, cy);
        }
        prev = { x: cx, y: cy };
        segDx = next.x - prev.x;
        segDy = next.y - prev.y;
        segLen = Math.hypot(segDx, segDy);
        drawing = !drawing;
        remaining = drawing ? dash : gap;
      }

      if (drawing) {
        dashed.lineTo(next.x, next.y);
      } else {
        dashed.moveTo(next.x, next.y);
      }
      remaining -= segLen;
      prev = next;
    }

    dashed.stroke({ color: stroke, width: strokeWidth, cap });
    // The shape's `g` already had `applyFill` called on it; we draw
    // dashes onto a separate Graphics and add it to the wrapper so
    // the dashes paint on top of any fill.
    wrapper.addChild(dashed);
  }

  /**
   * Sample a shape's outline into evenly-spaced points so the dash
   * walker has a smooth polyline to traverse. Densities are tuned per
   * shape to keep dash spacing visually consistent regardless of
   * size.
   */
  private samplePathPoints(
    shape: IOverlayElementShape["options"]["shape"],
    w: number,
    h: number,
    borderRadius: number,
  ): Array<{ x: number; y: number }> {
    void borderRadius;
    const segments = 192;

    if (shape === "circle") {
      const cx = w / 2;
      const cy = h / 2;
      const rx = w / 2;
      const ry = h / 2;
      const out: Array<{ x: number; y: number }> = [];
      for (let i = 0; i <= segments; i++) {
        const t = (i / segments) * Math.PI * 2;
        out.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
      }
      return out;
    }

    if (shape === "hexagon") {
      const cx = w / 2;
      const cy = h / 2;
      const rx = w / 2;
      const ry = h / 2;
      const corners: Array<{ x: number; y: number }> = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        corners.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
      }
      corners.push(corners[0]);
      return this.densifyPolyline(corners, segments);
    }

    if (shape === "rect" || shape === "rounded-rect") {
      const corners = [
        { x: 0, y: 0 },
        { x: w, y: 0 },
        { x: w, y: h },
        { x: 0, y: h },
        { x: 0, y: 0 },
      ];
      return this.densifyPolyline(corners, segments);
    }

    if (shape === "parallelogram") {
      const slant = Math.tan((15 * Math.PI) / 180) * h;
      const corners = [
        { x: slant, y: 0 },
        { x: w, y: 0 },
        { x: w - slant, y: h },
        { x: 0, y: h },
        { x: slant, y: 0 },
      ];
      return this.densifyPolyline(corners, segments);
    }

    if (shape === "chevron") {
      const tip = w * 0.25;
      const corners = [
        { x: 0, y: 0 },
        { x: w - tip, y: 0 },
        { x: w, y: h / 2 },
        { x: w - tip, y: h },
        { x: 0, y: h },
        { x: 0, y: 0 },
      ];
      return this.densifyPolyline(corners, segments);
    }

    return [];
  }

  /**
   * Walk a closed polyline (corners[0] == corners[last]) and
   * interpolate evenly along each segment so the total point count
   * is roughly `targetCount`, distributed by edge length.
   */
  private densifyPolyline(
    corners: Array<{ x: number; y: number }>,
    targetCount: number,
  ): Array<{ x: number; y: number }> {
    let perimeter = 0;
    for (let i = 1; i < corners.length; i++) {
      perimeter += Math.hypot(
        corners[i].x - corners[i - 1].x,
        corners[i].y - corners[i - 1].y,
      );
    }
    if (perimeter <= 0) return corners.slice();

    const out: Array<{ x: number; y: number }> = [];
    for (let i = 1; i < corners.length; i++) {
      const a = corners[i - 1];
      const b = corners[i];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const steps = Math.max(1, Math.round((len / perimeter) * targetCount));
      for (let s = 0; s < steps; s++) {
        const t = s / steps;
        out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
      }
    }
    out.push(corners[corners.length - 1]);
    return out;
  }

  /**
   * Render the glow as a tinted, blurred clone of the shape parked
   * behind the original. Same approach as before, but with two
   * adjustments:
   *  · `BlurFilter.strength` is scaled by ~3× because Pixi's blur
   *    kernel is much tighter than the equivalent CSS
   *    `drop-shadow(0 0 ${blur}px)`. The seed `blur` values
   *    (12-30) were authored against the CSS scale, so the Pixi
   *    render needs more strength to reach a similar halo width.
   *  · `glow.spread` is also folded into the blur for an extra wide
   *    bleed — Pixi has no spread, so we approximate.
   *
   * Tried `pixi-filters` `DropShadowFilter` directly on the wrapper,
   * which matches the DOM more accurately, but stacking that filter
   * alongside the overlay's mask can blank the entire stage in some
   * configurations. The clone-and-blur path is less faithful but
   * survives the masking.
   */
  private applyGlow(
    wrapper: Container,
    shape: IOverlayElementShape["options"]["shape"],
    w: number,
    h: number,
    borderRadius: number,
    glow: IOverlayGlow,
  ): void {
    try {
      const spread = glow.spread ?? 0;

      const halo = new Graphics();

      this.drawPath(halo, shape, w, h, borderRadius);

      halo.fill({ color: glow.color });

      // Scale outward from center to simulate spread
      const scale = w > 0 ? (w + spread * 2) / w : 1;

      halo.pivot.set(w / 2, h / 2);

      halo.position.set(w / 2, h / 2);

      halo.scale.set(scale);

      // Pixi `BlurFilter.strength` is in pixels, same units as CSS
      // `drop-shadow(0 0 Xpx)`. We add `spread` since Pixi has no
      // separate spread param, but no multiplier — the previous ×3
      // was masking a different bug and made wide-blur shapes (the
      // 30px panel glows in Hype) bleed across the whole canvas.
      const blurStrength = Math.max(1, (glow.blur ?? 0) + spread);
      const blur = new BlurFilter({ strength: blurStrength });

      halo.filters = [blur];

      // Drop the halo's alpha to ~55% so it reads as a softer glow,
      // closer to how CSS `drop-shadow` modulates the shadow against
      // the shape's own alpha. The clone-and-blur approach paints the
      // halo at fully opaque color which double-counts the glow on
      // shapes that already have a translucent fill (the Hype
      // diagonal panels with `rgba(0,240,255,0.55)` were the obvious
      // example — Pixi rendered a thick halo around them while CSS
      // showed almost none).
      halo.alpha = 0.55;

      wrapper.addChild(halo);
    } catch {
      // Silently skip glow if the filter cannot be constructed
    }
  }
}

export default OverlayShapeRenderer;
